import { create } from 'zustand';
import { rolePermissions } from '@shared/auth/permissions';
import type { Permission, RoleKey } from '@shared/auth/permissions';
import { clearTokens, hasSessionTokens } from '@shared/api/sessionTokens';

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: RoleKey;
  language?: string | null;
  departmentId?: number | null;
  departmentName?: string | null;
  permissions: Permission[];
}

type AuthStatus = 'loading' | 'unauthenticated' | 'awaitingOtp' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  user: CurrentUser | null;
  returnTo: string;
  otpChallengeId: string | null;
  otpDestination: string | null;
  otpExpiresAtUtc: string | null;
  passwordResetChallengeId: string | null;
  passwordResetDestination: string | null;
  passwordResetExpiresAtUtc: string | null;
  beginLogin: (challengeId: string, destination: string, expiresAtUtc: string) => void;
  beginPasswordReset: (challengeId: string, destination: string, expiresAtUtc: string) => void;
  clearOtpChallenge: () => void;
  clearPasswordResetChallenge: () => void;
  completeLogin: (user: CurrentUser) => void;
  beginSessionCheck: () => void;
  logout: () => void;
  setReturnTo: (path: string) => void;
  setAuthenticatedRole: (role: RoleKey) => void;
}

function userForRole(role: RoleKey): CurrentUser {
  return {
    id: 'USR-1001',
    name: 'Sara Al-Amiri',
    email: 'sara.a@helix.ae',
    role,
    language: 'en',
    departmentId: 4,
    departmentName: 'Customer Service',
    permissions: [...rolePermissions[role]],
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  status: hasSessionTokens() ? 'loading' : 'unauthenticated',
  user: null,
  returnTo: '/dashboard',
  otpChallengeId: null,
  otpDestination: null,
  otpExpiresAtUtc: null,
  passwordResetChallengeId: null,
  passwordResetDestination: null,
  passwordResetExpiresAtUtc: null,
  beginLogin: (otpChallengeId, otpDestination, otpExpiresAtUtc) =>
    set({ status: 'awaitingOtp', otpChallengeId, otpDestination, otpExpiresAtUtc }),
  beginPasswordReset: (
    passwordResetChallengeId,
    passwordResetDestination,
    passwordResetExpiresAtUtc,
  ) => set({ passwordResetChallengeId, passwordResetDestination, passwordResetExpiresAtUtc }),
  clearOtpChallenge: () =>
    set({
      status: 'unauthenticated',
      otpChallengeId: null,
      otpDestination: null,
      otpExpiresAtUtc: null,
    }),
  clearPasswordResetChallenge: () =>
    set({
      passwordResetChallengeId: null,
      passwordResetDestination: null,
      passwordResetExpiresAtUtc: null,
    }),
  completeLogin: (user) => set({ status: 'authenticated', user }),
  beginSessionCheck: () =>
    set({
      status: 'loading',
      user: null,
      otpChallengeId: null,
      otpDestination: null,
      otpExpiresAtUtc: null,
      passwordResetChallengeId: null,
      passwordResetDestination: null,
      passwordResetExpiresAtUtc: null,
    }),
  logout: () => {
    clearTokens();
    set({
      status: 'unauthenticated',
      user: null,
      otpChallengeId: null,
      otpDestination: null,
      otpExpiresAtUtc: null,
      passwordResetChallengeId: null,
      passwordResetDestination: null,
      passwordResetExpiresAtUtc: null,
    });
  },
  setReturnTo: (returnTo) => set({ returnTo }),
  setAuthenticatedRole: (role) => set({ status: 'authenticated', user: userForRole(role) }),
}));
